import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stack,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  PersonAdd,
  Phone,
  Email,
  LocationOn,
  TrendingUp,
  Notes,
  History,
  MoreVert,
  Send,
  Check,
  Close,
  AttachMoney,
  Home,
  CalendarMonth,
  Source as SourceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../services/api';

const PIPELINE_STAGES = [
  { value: 'new', label: 'New', color: '#2196f3' },
  { value: 'contacted', label: 'Contacted', color: '#ff9800' },
  { value: 'qualified', label: 'Qualified', color: '#9c27b0' },
  { value: 'consultation_scheduled', label: 'Consultation Scheduled', color: '#00bcd4' },
  { value: 'consultation_completed', label: 'Consultation Completed', color: '#4caf50' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: '#673ab7' },
  { value: 'negotiating', label: 'Negotiating', color: '#ff5722' },
  { value: 'contracted', label: 'Contracted', color: '#4caf50' },
  { value: 'lost', label: 'Lost', color: '#f44336' },
];

const URGENCY_CONFIG = {
  urgent: { label: 'Urgent', color: 'error', icon: '🔥' },
  high: { label: 'High', color: 'warning', icon: '⚡' },
  medium: { label: 'Medium', color: 'info', icon: '📌' },
  low: { label: 'Low', color: 'default', icon: '📋' },
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchLeadDetails();
  }, [id]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/leads/${id}`);
      setLead(response.data);
    } catch (error) {
      console.error('Error fetching lead details:', error);
      showSnackbar('Failed to load lead details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/leads/${id}/status`, { status: newStatus });
      await fetchLeadDetails();
      showSnackbar('Status updated successfully');
      setStatusMenuAnchor(null);
    } catch (error) {
      console.error('Error updating status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    try {
      setSubmittingNote(true);
      await api.post(`/leads/${id}/notes`, { note: noteText });
      setNoteText('');
      await fetchLeadDetails();
      showSnackbar('Note added successfully');
    } catch (error) {
      console.error('Error adding note:', error);
      showSnackbar('Failed to add note', 'error');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleConvertToClient = async () => {
    try {
      await api.post(`/leads/${id}/convert`);
      showSnackbar('Lead converted to client successfully');
      setConvertDialogOpen(false);
      navigate('/clients');
    } catch (error) {
      console.error('Error converting lead:', error);
      showSnackbar('Failed to convert lead to client', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/leads/${id}`);
      showSnackbar('Lead deleted successfully');
      navigate('/leads');
    } catch (error) {
      console.error('Error deleting lead:', error);
      showSnackbar('Failed to delete lead', 'error');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6" color="text.secondary">
          Lead not found
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/leads')} sx={{ mt: 2 }}>
          Back to Leads
        </Button>
      </Box>
    );
  }

  const urgencyConfig = URGENCY_CONFIG[lead.urgency] || URGENCY_CONFIG.medium;
  const currentStage = PIPELINE_STAGES.find((s) => s.value === lead.status);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/leads')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" flexGrow={1}>
          Lead Details
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Edit />}
          onClick={() => setEditDialogOpen(true)}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
                      {lead.first_name?.[0]}{lead.last_name?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {lead.first_name} {lead.last_name}
                      </Typography>
                      <Stack direction="row" spacing={1} mt={1}>
                        <Chip
                          label={currentStage?.label || lead.status || 'New'}
                          sx={{
                            bgcolor: currentStage?.color || '#9e9e9e',
                            color: 'white',
                          }}
                        />
                        <Chip
                          label={urgencyConfig.label}
                          color={urgencyConfig.color}
                          icon={<span style={{ fontSize: '0.875rem' }}>{urgencyConfig.icon}</span>}
                        />
                        {lead.lead_score && (
                          <Chip
                            label={`Score: ${lead.lead_score}`}
                            color={
                              lead.lead_score >= 80
                                ? 'success'
                                : lead.lead_score >= 50
                                ? 'warning'
                                : 'default'
                            }
                            icon={<TrendingUp />}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PersonAdd />}
                    onClick={() => setConvertDialogOpen(true)}
                  >
                    Convert to Client
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Email color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">{lead.email}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  {lead.phone && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Phone color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">{lead.phone}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {lead.preferred_contact && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Send color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Preferred Contact
                          </Typography>
                          <Typography variant="body1" textTransform="capitalize">
                            {lead.preferred_contact}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {lead.source && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <SourceIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Source
                          </Typography>
                          <Typography variant="body1">{lead.source}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Property Details
                </Typography>
                <Grid container spacing={2}>
                  {lead.property_address && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <LocationOn color="action" sx={{ mt: 0.5 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Address
                          </Typography>
                          <Typography variant="body1">
                            {lead.property_address}
                            {lead.property_city && (
                              <>
                                <br />
                                {lead.property_city}
                                {lead.property_state && `, ${lead.property_state}`}
                                {lead.property_zip && ` ${lead.property_zip}`}
                              </>
                            )}
                            {lead.property_county && (
                              <>
                                <br />
                                County: {lead.property_county}
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {lead.estimated_value && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AttachMoney color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Estimated Value
                          </Typography>
                          <Typography variant="body1">{lead.estimated_value}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {lead.estimated_items && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Home color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Estimated Items
                          </Typography>
                          <Typography variant="body1">{lead.estimated_items}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {lead.message && (
                  <Box mt={3}>
                    <Typography variant="caption" color="text.secondary">
                      Initial Message
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                      <Typography variant="body2">{lead.message}</Typography>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Notes & Timeline
                </Typography>
                <form onSubmit={handleAddNote}>
                  <Box display="flex" gap={1} mb={3}>
                    <TextField
                      fullWidth
                      placeholder="Add a note..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      multiline
                      rows={2}
                      variant="outlined"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submittingNote || !noteText.trim()}
                      sx={{ minWidth: 100 }}
                    >
                      {submittingNote ? 'Adding...' : 'Add Note'}
                    </Button>
                  </Box>
                </form>

                <List>
                  {lead.notes && lead.notes.length > 0 ? (
                    lead.notes.map((note, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start">
                          <ListItemAvatar>
                            <Avatar>
                              <Notes />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={note.note || note.text || note}
                            secondary={
                              note.created_at
                                ? format(new Date(note.created_at), 'MMM dd, yyyy hh:mm a')
                                : 'Just now'
                            }
                          />
                        </ListItem>
                        {index < lead.notes.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No notes yet"
                        secondary="Add your first note to track interactions with this lead"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Pipeline Status
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<MoreVert />}
                  onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                  sx={{ mb: 2 }}
                >
                  Change Status
                </Button>
                <Menu
                  anchorEl={statusMenuAnchor}
                  open={Boolean(statusMenuAnchor)}
                  onClose={() => setStatusMenuAnchor(null)}
                >
                  {PIPELINE_STAGES.map((stage) => (
                    <MenuItem
                      key={stage.value}
                      onClick={() => handleStatusChange(stage.value)}
                      selected={lead.status === stage.value}
                    >
                      <Chip
                        label={stage.label}
                        size="small"
                        sx={{
                          bgcolor: stage.color,
                          color: 'white',
                          mr: 1,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Menu>

                <Box>
                  {PIPELINE_STAGES.map((stage, index) => {
                    const isActive = lead.status === stage.value;
                    const isPast =
                      PIPELINE_STAGES.findIndex((s) => s.value === lead.status) > index;
                    return (
                      <Box key={stage.value} display="flex" alignItems="center" mb={1}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: isActive || isPast ? stage.color : 'grey.300',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1,
                          }}
                        >
                          {isPast && <Check sx={{ color: 'white', fontSize: 16 }} />}
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={isActive ? 'bold' : 'normal'}
                          color={isActive || isPast ? 'text.primary' : 'text.secondary'}
                        >
                          {stage.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Activity Log
                </Typography>
                <List dense>
                  {lead.created_at && (
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <History fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Lead created"
                        secondary={format(new Date(lead.created_at), 'MMM dd, yyyy hh:mm a')}
                      />
                    </ListItem>
                  )}
                  {lead.updated_at && lead.updated_at !== lead.created_at && (
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                          <Edit fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Last updated"
                        secondary={format(new Date(lead.updated_at), 'MMM dd, yyyy hh:mm a')}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Quick Actions
                </Typography>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Email />}
                    href={`mailto:${lead.email}`}
                  >
                    Send Email
                  </Button>
                  {lead.phone && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Phone />}
                      href={`tel:${lead.phone}`}
                    >
                      Call
                    </Button>
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CalendarMonth />}
                    onClick={() => navigate('/calendar')}
                  >
                    Schedule Meeting
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <EditLeadDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        lead={lead}
        onSuccess={() => {
          fetchLeadDetails();
          setEditDialogOpen(false);
          showSnackbar('Lead updated successfully');
        }}
      />

      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)}>
        <DialogTitle>Convert Lead to Client</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to convert this lead to a client? This will create a new client
            record with the lead's information.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConvertToClient} variant="contained" color="success">
            Convert to Client
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this lead? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete Lead
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const EditLeadDialog = ({ open, onClose, lead, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_county: '',
    source: '',
    estimated_value: '',
    estimated_items: '',
    urgency: 'medium',
    preferred_contact: 'email',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lead) {
      setFormData({
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        property_address: lead.property_address || '',
        property_city: lead.property_city || '',
        property_state: lead.property_state || '',
        property_zip: lead.property_zip || '',
        property_county: lead.property_county || '',
        source: lead.source || '',
        estimated_value: lead.estimated_value || '',
        estimated_items: lead.estimated_items || '',
        urgency: lead.urgency || 'medium',
        preferred_contact: lead.preferred_contact || 'email',
        message: lead.message || '',
      });
    }
  }, [lead]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await api.put(`/leads/${lead.id}`, formData);
      onSuccess();
    } catch (err) {
      console.error('Error updating lead:', err);
      setError(err.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Lead</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Property Address"
                name="property_address"
                value={formData.property_address}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="property_city"
                value={formData.property_city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                name="property_state"
                value={formData.property_state}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="property_zip"
                value={formData.property_zip}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="County"
                name="property_county"
                value={formData.property_county}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Source"
                name="source"
                value={formData.source}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Property Value"
                name="estimated_value"
                value={formData.estimated_value}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Number of Items"
                name="estimated_items"
                type="number"
                value={formData.estimated_items}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Preferred Contact Method"
                name="preferred_contact"
                value={formData.preferred_contact}
                onChange={handleChange}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="text">Text</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message / Notes"
                name="message"
                value={formData.message}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Updating...' : 'Update Lead'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LeadDetail;
